import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '../utils/cn';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface CarouselProps {
  /** Show dot indicators */
  dots?: boolean;
  /** Show arrow buttons */
  arrows?: boolean;
  /** Custom previous arrow */
  prevArrow?: React.ReactNode;
  /** Custom next arrow */
  nextArrow?: React.ReactNode;
  /** Autoplay (not implemented yet — add embla-carousel-autoplay if needed) */
  autoplay?: boolean;
  /** Callback after slide change */
  afterChange?: (index: number) => void;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Slides as children */
  children?: React.ReactNode;
}

export function Carousel({
  dots = false,
  arrows = false,
  afterChange,
  className,
  style,
  children,
}: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

  const slides = React.Children.toArray(children).filter(Boolean);

  const scrollPrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = React.useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    const idx = emblaApi.selectedScrollSnap();
    setSelectedIndex(idx);
    afterChange?.(idx);
  }, [emblaApi, afterChange]);

  React.useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <div className={cn('relative group', className)} style={style}>
      {/* Viewport */}
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {slides.map((slide, index) => (
            <div
              key={index}
              className="flex-[0_0_100%] min-w-0"
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {/* Arrow buttons */}
      {arrows && slides.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            className={cn(
              'absolute left-2 top-1/2 -translate-y-1/2 z-10',
              '!p-2 !h-auto !border-0 !tracking-normal !normal-case',
              'bg-white/70 hover:bg-white/90 text-foreground',
              'rounded-full shadow-md backdrop-blur-sm',
              'opacity-0 group-hover:opacity-100 transition-opacity duration-fast'
            )}
            aria-label="Föregående"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={scrollNext}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 z-10',
              '!p-2 !h-auto !border-0 !tracking-normal !normal-case',
              'bg-white/70 hover:bg-white/90 text-foreground',
              'rounded-full shadow-md backdrop-blur-sm',
              'opacity-0 group-hover:opacity-100 transition-opacity duration-fast'
            )}
            aria-label="Nästa"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {dots && slides.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={cn(
                'h-2 w-2 rounded-full transition-all duration-fast ease-smooth',
                '!p-0 !border-0 !tracking-normal !normal-case',
                index === selectedIndex
                  ? 'bg-accent w-4'
                  : 'bg-gray-300 hover:bg-gray-400'
              )}
              aria-label={`Gå till bild ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
